import { useEffect } from 'react';
import styled from '@emotion/styled';
import useTranslation from 'next-translate/useTranslation';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import { Button, Tooltip, Typography } from '@mui/material';
import { useSiteContext } from 'src/store';
import theme from '../../themes';
declare global {
  interface Window {
    loadService: ((callback?: () => void) => void) | undefined;
    _reciteLoaded?: () => void;
    Recite?: {
      load: (options: Record<string, unknown>) => void;
      enable: () => void;
      Event: { subscribe: (event: string, handler: () => void) => void };
    };
  }
}

const LaunchButton = styled(Button)(() => ({
  minWidth: 'fit-content',
  width: 'auto',
  height: 46,
  borderRadius: 9999,
  padding: `0 ${theme.spacing(1.5)}`,
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  border: `2px solid ${theme.palette.primary.main}`,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.paper,
  transition: 'border-color 0.2s',
  '&:hover': {
    borderColor: theme.palette.primary.dark,
    backgroundColor: theme.palette.action.hover,
  },
}));

const SERVICE_URL = '//api.reciteme.com/asset/js?v=3.7.2p&key=';
const ENABLE_FRAGMENT = '#reciteEnable';
const loaded: string[] = [];

const getCookie = (name: string): string | null => {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trimStart();
    if (trimmed.startsWith(prefix)) return trimmed.substring(prefix.length);
  }
  return null;
};

const deleteReciteCookies = () => {
  for (const part of document.cookie.split(';')) {
    const name = part.trimStart().split('=')[0];
    if (name.startsWith('Recite.')) {
      document.cookie = `${name}=; max-age=0; path=/`;
    }
  }
};

const loadScript = (src: string, onLoad: () => void, onError: () => void) => {
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.onload = onLoad;
  script.onerror = onError;
  script.src = src;
  document.head.appendChild(script);
};

const ReciteMeReader = () => {
  const { t } = useTranslation();
  const { reciteMeConfiguration } = useSiteContext();

  const customerKey = reciteMeConfiguration?.customerKey;

  useEffect(() => {
    if (!reciteMeConfiguration?.enabled || !customerKey) {
      deleteReciteCookies();
    }
  }, [reciteMeConfiguration?.enabled, customerKey]);

  useEffect(() => {
    if (!reciteMeConfiguration?.enabled || !customerKey) return;

    const onScriptLoad = (callback?: () => void) => {
      if (!globalThis.Recite) return;
      globalThis._reciteLoaded?.();
      callback?.();
      globalThis.Recite.load({});
      globalThis.Recite.Event.subscribe('Recite:load', () =>
        globalThis.Recite?.enable()
      );
      globalThis.Recite.Event.subscribe('Recite:toolbar:close', () =>
        deleteReciteCookies()
      );
    };

    globalThis.loadService = (callback?: () => void) => {
      const src = `${SERVICE_URL}${customerKey}`;
      if (loaded.includes(src)) return;
      loaded.push(src);
      loadScript(
        src,
        () => onScriptLoad(callback),
        () => loaded.splice(loaded.indexOf(src), 1)
      );
    };

    if (
      getCookie('Recite.Persist') === 'true' ||
      globalThis.location.hash === ENABLE_FRAGMENT
    ) {
      globalThis.loadService();
    }

    globalThis._reciteLoaded = () => {
      // Fixed close button.
      globalThis.Recite.Event.subscribe('Controls:loaded', () => {
        document
          .querySelector('#recite-close')
          ?.addEventListener('click', () => {
            setTimeout(() => {
              const htmlEl = document.querySelector('html');
              const modalEl = document.querySelector('#recite-modal');
              if (htmlEl && modalEl) {
                htmlEl.prepend(modalEl);
              }
            }, 200);
          });
        const headerElement = globalThis.Recite?.Sizzle('#recite-header')[0];
        const htmlElement = globalThis.Recite?.Sizzle('html')[0];
        if (htmlElement && headerElement) {
          htmlElement.prepend(headerElement);
        }
      });
    };

    return () => {
      globalThis.loadService = undefined;
    };
  }, [customerKey]);

  if (!reciteMeConfiguration?.enabled || !customerKey) {
    return null;
  }

  return (
    <Tooltip title={t('reciteMe.accessibilityOptions')}>
      <span>
        <LaunchButton
          type="button"
          onClick={() => globalThis.loadService?.()}
          aria-label={t('reciteMe.launchButtonLabel')}
        >
          <Typography component="span" sx={{ whiteSpace: 'nowrap' }}>
            {t('reciteMe.accessibilityTitle')}
          </Typography>
          <AccessibilityIcon
            sx={{ fontSize: 28, color: 'primary.main', m: 0, flexShrink: 0 }}
            aria-hidden
          />
        </LaunchButton>
      </span>
    </Tooltip>
  );
};

export default ReciteMeReader;
